/**
 * HTML5 drag-and-drop for Browse: moving a document into a folder, and
 * re-parenting a folder.
 *
 * Two payload types, distinguished by MIME: `DRAG_DOC_MIME` (move a document
 * into a folder) and `DRAG_FOLDER_MIME` (re-parent a folder). Every folder row
 * in the tree, the table and the grid is both a source and a target;
 * `FolderDragContext` holds the in-flight drag so a row can grey itself out
 * for an illegal destination during `dragover`, when the payload is not yet
 * readable.
 *
 * The Desktop does NOT use this: it drags with pointer events and keeps
 * native drag-and-drop for files dropped from the computer.
 */
import { createContext, useContext, useState } from 'react';
import type { VaultDocumentFolderDto } from '@/lib/api';

/** Custom MIME type for HTML5 drag-and-drop. The dataTransfer carries the
 * documentId being dragged; folder rows + grid tiles register as drop
 * targets via the same MIME so we don't accidentally accept native file
 * drags from the OS. */
export const DRAG_DOC_MIME = 'application/x-fias-vault-document-id';

/** Custom MIME for a folder being dragged. Kept distinct from
 * `DRAG_DOC_MIME` so a row can tell the two payloads apart — and so
 * neither accepts a native OS file drag. */
export const DRAG_FOLDER_MIME = 'application/x-fias-vault-folder-id';

export interface FolderDragContextValue {
  /** Folder currently under the cursor's drag, or null when idle. */
  draggingFolderId: string | null;
  startDrag: (folderId: string) => void;
  endDrag: () => void;
  /** May the in-flight folder land in `targetFolderId` (`null` = the
   * Holder root)? False when nothing is being dragged. */
  canDropInto: (targetFolderId: string | null) => boolean;
  /** Commit the move. `draggedFolderId` comes from the drop event's
   * `dataTransfer` — the authoritative payload — rather than from
   * `draggingFolderId`, which is only a render hint. */
  dropInto: (targetFolderId: string | null, draggedFolderId: string) => void;
}

/** Null outside a provider, which disables folder drag-and-drop but
 * leaves every row rendering normally — the exported row components
 * stay usable standalone. */
export const FolderDragContext = createContext<FolderDragContextValue | null>(null);

export function useFolderDrag(): FolderDragContextValue | null {
  return useContext(FolderDragContext);
}

interface FolderRowDndOptions {
  /** Where a dropped folder should land: a folder id, `null` for the
   * Holder root, or `undefined` to reject folder drops entirely
   * (Trash). */
  dropTarget: string | null | undefined;
  /** The folder this row can be dragged as. Omit on rows that aren't
   * folders; protected folders are refused here rather than by the
   * caller. */
  dragFolder?: VaultDocumentFolderDto;
  /** Existing document-drop handler. When omitted the row refuses
   * document drags but may still accept folder drops. */
  onDropDocument?: (documentId: string) => void;
}

/** Drag-and-drop wiring shared by every folder row — the tree rows,
 * the table rows, and the grid tiles all behave identically, so the
 * handlers live here once and get spread onto whichever element the
 * row happens to render. */
export function useFolderRowDnd({ dropTarget, dragFolder, onDropDocument }: FolderRowDndOptions) {
  const folderDrag = useFolderDrag();
  const [dropHover, setDropHover] = useState(false);

  const acceptsFolderDrop =
    folderDrag !== null && dropTarget !== undefined && folderDrag.canDropInto(dropTarget);
  const draggableFolder = folderDrag && dragFolder && !dragFolder.isProtected ? dragFolder : null;
  const isDragSource = !!dragFolder && folderDrag?.draggingFolderId === dragFolder.folderId;

  const dragProps = draggableFolder
    ? {
        draggable: true,
        onDragStart: (e: React.DragEvent) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData(DRAG_FOLDER_MIME, draggableFolder.folderId);
          folderDrag!.startDrag(draggableFolder.folderId);
        },
        // Fires even on a cancelled drag, so the provider never gets
        // stuck holding a stale drag.
        onDragEnd: () => folderDrag!.endDrag(),
      }
    : {};

  const dropProps =
    onDropDocument || acceptsFolderDrop
      ? {
          onDragOver: (e: React.DragEvent) => {
            // `getData` is unreadable during dragover — the browser
            // only exposes `types` — so the payload is identified by
            // MIME and legality comes from the provider's drag state.
            const types = e.dataTransfer.types;
            if (types.includes(DRAG_FOLDER_MIME)) {
              if (!acceptsFolderDrop) return;
            } else if (!(types.includes(DRAG_DOC_MIME) && onDropDocument)) {
              return;
            }
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDropHover(true);
          },
          onDragLeave: () => setDropHover(false),
          onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            setDropHover(false);
            const draggedFolderId = e.dataTransfer.getData(DRAG_FOLDER_MIME);
            if (draggedFolderId) {
              if (folderDrag && dropTarget !== undefined) {
                folderDrag.dropInto(dropTarget, draggedFolderId);
              }
              return;
            }
            const documentId = e.dataTransfer.getData(DRAG_DOC_MIME);
            if (documentId && onDropDocument) onDropDocument(documentId);
          },
        }
      : {};

  return {
    dropHover,
    isDragSource,
    isDraggable: !!draggableFolder,
    dndProps: { ...dragProps, ...dropProps },
  };
}
